import { BiSolidUserAccount } from "solid-icons/bi";
import { type Component, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { AvatarToDeprecate } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { CvCandidate } from "./types";

/**
 * Il modale "Come vuoi procedere?" dei CV anonimi — gemello di quello di
 * "Contatta". Si apre alla PRIMA scelta e offre due strade: scaricare subito
 * quell'unico CV, oppure aprire una selezione e scaricare tutto in una volta.
 *
 * Il candidato non è ancora in selezione mentre il modale è aperto: sono le due
 * strade a deciderne la sorte, e chiuderlo senza scegliere lo scarta. Per
 * questo qui i testi sono sempre al singolare — contare la selezione darebbe
 * zero — e non c'è nulla da mostrare degli slot.
 *
 * Condiviso da ricerca attiva e talent pool. Il trigger no: nella ricerca è la
 * CTA a tutta larghezza del dettaglio, nel pool il bottone "Anonimizza CV" in
 * riga sulla card. Qui vive solo il modale, identico sulle due superfici.
 */
export const CvProceedDialog: Component<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Il candidato appena scelto: è la sua faccia che il modale mostra. */
  candidate: CvCandidate;
  onDownloadNow: () => void;
  onSelectMore: () => void;
}> = (props) => {
  const { t } = useI18n();

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent class="max-w-md">
        <DialogHeader>
          <div class="flex items-center gap-3 mb-3">
            <AvatarToDeprecate
              src={props.candidate.avatar_url ?? ""}
              alt={props.candidate.full_name ?? ""}
              class="h-10 w-10 shrink-0"
              fallback={<BiSolidUserAccount class="w-5 h-5" />}
            />
            <div class="min-w-0">
              <p class="font-semibold text-sm truncate">
                {props.candidate.full_name}
              </p>
              <Show when={props.candidate.job_title}>
                <p class="text-xs text-muted-foreground line-clamp-2 break-words">
                  {props.candidate.job_title}
                </p>
              </Show>
            </div>
          </div>
          <DialogTitle>
            {t("company.search.freelancers.result.cv_dialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("company.search.freelancers.result.cv_dialog.description", {
              name: props.candidate.full_name ?? "",
            })}
          </DialogDescription>
        </DialogHeader>
        <div class="grid grid-cols-1 gap-3 mt-2">
          <button
            type="button"
            class="flex flex-col items-start gap-1 p-4 rounded-lg border border-gray-200 hover:border-primary hover:bg-primary/5 transition-colors text-left"
            onClick={() => props.onDownloadNow()}
          >
            {/* Sempre al singolare: il modale si apre solo alla prima scelta,
                e il candidato non è ancora in selezione — contarla darebbe
                zero. Chi ne vuole più d'uno passa da "aggiungi altri". */}
            <span class="font-medium text-sm">
              {t("company.search.freelancers.result.cv_dialog.download_one")}
            </span>
            <span class="text-xs text-muted-foreground">
              {t(
                "company.search.freelancers.result.cv_dialog.download_desc_one",
              )}
            </span>
          </button>
          {/* Sempre offerta: il modale vive solo a selezione vuota, quindi di
              slot liberi ce ne sono per definizione. */}
          <button
            type="button"
            class="flex flex-col items-start gap-1 p-4 rounded-lg border border-gray-200 hover:border-primary hover:bg-primary/5 transition-colors text-left"
            onClick={() => props.onSelectMore()}
          >
            <span class="font-medium text-sm">
              {t("company.search.freelancers.result.cv_dialog.select_more")}
            </span>
            <span class="text-xs text-muted-foreground">
              {t(
                "company.search.freelancers.result.cv_dialog.select_more_desc",
              )}
            </span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
